import { useFormContext, useFormState, useWatch } from "react-hook-form";

import { X } from "@/shared/foundation/icons";
import {
  Card,
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";

import {
  IMPLICIT_DATASET_DESCRIPTION,
  IMPLICIT_DATASET_PLACEHOLDER,
} from "../../Agents/buildRequestTemplate";
import {
  type AgentFormValues,
  type GoalFormValues,
  type GoalKind,
} from "../agentFormSchema";

import { CreateEventsGoalForm } from "./CreateEventsGoalForm";
import { DatasetSummaryGoalForm } from "./DatasetSummaryGoalForm";
import { DatasetTriageGoalForm } from "./DatasetTriageGoalForm";
import { GOAL_KIND_COPY, GOAL_KINDS } from "./goalKinds";
import styles from "./goals.module.css";
import { ImplicitBinding } from "./ImplicitBinding";

export interface GoalCardProps {
  index: number;
  onRemove: () => void;
  /** Replaces this goal with a fresh one of `kind`, keeping its position. */
  onChangeKind: (kind: GoalKind) => void;
}

/**
 * One goal: the kind it is, and the fields that kind needs.
 *
 * The section owns which goals exist; the card owns the fields of its own.
 */
export function GoalCard({ index, onRemove, onChangeKind }: GoalCardProps) {
  const { control, getFieldState } = useFormContext<AgentFormValues>();
  const goal = useWatch<AgentFormValues>({
    control,
    name: `goals.${index}`,
  }) as GoalFormValues;
  const kind = goal.goal_type;
  const formState = useFormState<AgentFormValues>({ control });
  const kindError = getFieldState(`goals.${index}.goal_type`, formState).error;

  return (
    <Card.Root
      size="compact"
      data-testid={`agent-goal-${index}`}
      data-kind={kind}
    >
      <Card.Header>
        {/* The card's title in the page outline; the blocks under it are h4s. */}
        <h3 className={styles.goalHeading}>
          <Card.Title>Goal</Card.Title>{" "}
          <Card.Summary>{GOAL_KIND_COPY[kind].label}</Card.Summary>
        </h3>
        <Card.Actions>
          <RobotoButton
            variant="ghost"
            icon={<X aria-hidden />}
            aria-label="Remove this goal"
            onClick={onRemove}
            data-testid={`agent-goal-${index}-remove`}
          />
        </Card.Actions>
      </Card.Header>
      <Card.Body className={styles.goalBody}>
        <RadioGroup<GoalKind>
          appearance="card"
          size="md"
          aria-label="What this goal is"
          value={kind}
          onValueChange={onChangeKind}
          error={kindError?.message}
        >
          {GOAL_KINDS.map((offered) => {
            const { label, description, icon: Icon } = GOAL_KIND_COPY[offered];
            return (
              <Radio
                key={offered}
                value={offered}
                label={label}
                description={description}
                icon={<Icon aria-hidden size={16} />}
              />
            );
          })}
        </RadioGroup>

        {/* Every kind of goal works on one dataset, and always the one chosen
            at launch — so the binding is stated once, above the kind's own
            fields, rather than inside each of them. */}
        <ImplicitBinding
          label="Dataset"
          placeholder={IMPLICIT_DATASET_PLACEHOLDER}
          hint={IMPLICIT_DATASET_DESCRIPTION}
        />

        {kind === "dataset_summary" && <DatasetSummaryGoalForm index={index} />}
        {kind === "dataset_triage" && <DatasetTriageGoalForm index={index} />}
        {kind === "create_events" && <CreateEventsGoalForm index={index} />}
      </Card.Body>
    </Card.Root>
  );
}
